import { RichTextEditor, Link } from "@mantine/tiptap"
import { useEditor } from "@tiptap/react"
import Highlight from "@tiptap/extension-highlight"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import { useTranslation } from "react-i18next"
import { EmojiClickData } from "emoji-picker-react"
import { EmojiPickerDropdown } from "../EmojiPickerDropdown"
import "./module.css"

interface Props {
  value?: string
  onChange: (value: string) => void
  style?: React.CSSProperties
  showEmojiPicker?: boolean
  disabled?: boolean
}

export function TextEditor({
  value,
  onChange,
  style,
  showEmojiPicker = false,
  disabled = false,
}: Props) {
  const { t } = useTranslation("app_server")

  const editor = useEditor({
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange && onChange(editor.getHTML())
    },
    extensions: [StarterKit, Underline, Link, Highlight],
    content: value,
  })

  const insertEmoji = (emojiClick: EmojiClickData) => {
    if (!editor) return
    editor.chain().focus().insertContent(emojiClick.emoji).run()
  }

  return (
    <RichTextEditor
      flex="1 1 100%"
      display="flex"
      style={{ flexDirection: "column", ...style }}
      editor={editor}
      labels={{
        boldControlLabel: t("boldControlLabel"),
        italicControlLabel: t("italicControlLabel"),
        underlineControlLabel: t("underlineControlLabel"),
        strikeControlLabel: t("strikeControlLabel"),
        clearFormattingControlLabel: t("clearFormattingControlLabel"),
        hrControlLabel: t("hrControlLabel"),
        bulletListControlLabel: t("bulletListControlLabel"),
        orderedListControlLabel: t("orderedListControlLabel"),
        linkEditorSave: t("Save"),
        unlinkControlLabel: t("unlinkControlLabel"),
      }}
    >
      <RichTextEditor.Toolbar sticky stickyOffset={60}>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Underline />
          <RichTextEditor.Strikethrough />
          <RichTextEditor.Highlight />
          <RichTextEditor.ClearFormatting />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Hr />
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Link
            popoverProps={{ width: 700 }}
            styles={{
              linkEditorDropdown: {
                width: "min(90vw, 700px)",
              },
              linkEditor: {
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                alignItems: "end",
                width: "100%",
              },
            }}
          />
          <RichTextEditor.Unlink />
        </RichTextEditor.ControlsGroup>

        {showEmojiPicker && (
          <RichTextEditor.ControlsGroup>
            <EmojiPickerDropdown
              iconProps={{ stroke: 1, size: 20 }}
              position="left"
              onSelect={insertEmoji}
              actionIconProps={{
                size: 26,
                style: {
                  border: "1px solid var(--mantine-color-dark-4)",
                },
                color: "var(--mantine-color-dark-1)",
                variant: "light",
              }}
            />
          </RichTextEditor.ControlsGroup>
        )}
      </RichTextEditor.Toolbar>

      <RichTextEditor.Content flex="0 0 100%" />
    </RichTextEditor>
  )
}
